/**
 * UI Settings Store - 讓玩家調整各 UI 元素的縮放倍率
 * 使用 zustand + localStorage 持久化
 */

import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

export interface UISettings {
  battlefieldScale: number;  // 戰場卡牌大小 (0.8-1.5, default 1.0)
  handScale: number;         // 手牌大小 (0.8-2.0, default 1.0)
  fontScale: number;         // 文字大小 (0.8-1.5, default 1.0)
  infoPanelScale: number;    // 資訊面板大小 (0.8-1.5, default 1.0)
}

export interface UISettingsStore extends UISettings {
  // Actions
  setBattlefieldScale: (value: number) => void;
  setHandScale: (value: number) => void;
  setFontScale: (value: number) => void;
  setInfoPanelScale: (value: number) => void;
  resetToDefaults: () => void;

  // Internal version counter to detect changes in Phaser
  _version: number;
}

const DEFAULT_SETTINGS: UISettings = {
  battlefieldScale: 1.0,
  handScale: 1.0,
  fontScale: 1.0,
  infoPanelScale: 1.0,
};

function clamp(value: number, min: number, max: number): number {
  return Math.round(Math.max(min, Math.min(max, value)) * 100) / 100;
}

export const useUISettingsStore = create<UISettingsStore>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
        ...DEFAULT_SETTINGS,
        _version: 0,

        setBattlefieldScale: (value: number) => {
          set((s) => ({ battlefieldScale: clamp(value, 0.8, 1.5), _version: s._version + 1 }));
        },

        setHandScale: (value: number) => {
          set((s) => ({ handScale: clamp(value, 0.8, 2.0), _version: s._version + 1 }));
        },

        setFontScale: (value: number) => {
          set((s) => ({ fontScale: clamp(value, 0.8, 1.5), _version: s._version + 1 }));
        },

        setInfoPanelScale: (value: number) => {
          set((s) => ({ infoPanelScale: clamp(value, 0.8, 1.5), _version: s._version + 1 }));
        },

        resetToDefaults: () => {
          set((s) => ({ ...DEFAULT_SETTINGS, _version: s._version + 1 }));
        },
      }),
      {
        name: 'xinglingwang-ui-settings',
        partialize: (state) => ({
          battlefieldScale: state.battlefieldScale,
          handScale: state.handScale,
          fontScale: state.fontScale,
          infoPanelScale: state.infoPanelScale,
        }),
      }
    ),
    { name: 'UISettingsStore' }
    )
  )
);
